/**
 * Hook for accessing experiment element visibility
 * 
 * Context: Address Page
 * Components use this to check if they should render based on
 * the current experiment state and viewport.
 */

import { useMemo } from 'react';
import { AllowedExperimentKey } from '@tenants/types';
import { useExperimentVariation, useExperimentTracking } from 'ui/experimental-section/hooks';
import { useIsMobile } from 'hooks/use-size-class';
import { useTenantId } from 'hooks/use-tenant-id';
import { createExperimentElementVisibility } from './experiment-element-visibility';
import type { ExperimentState, Viewport } from './types';

/**
 * Hook that provides experiment element visibility management
 * 
 * Context: Address Page (LISTING_HERO, STICKY_HEADER_TABS experiments)
 * 
 * Automatically detects viewport (mobile vs desktop) and experiment state.
 * Tracks experiment views for analytics (once per experiment per page load).
 * Safe to use in tests - returns default visibility manager if experiments aren't available.
 * 
 * @example
 * ```tsx
 * function SwiperCarousel() {
 *   const visibility = useExperimentElementVisibility();
 *   
 *   const showShareButton = visibility.shouldShow(
 *     ExperimentElement.CAROUSEL_SHARE_BUTTON
 *   );
 *   
 *   return (
 *     <div>
 *       {showShareButton && <ShareButton />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useExperimentElementVisibility() {
  // Detect viewport
  const isMobile = useIsMobile();
  const viewport: Viewport = isMobile ? 'mobile' : 'desktop';
  
  // Get tenant ID for tracking
  const tenantId = useTenantId();
  
  // Get experiment variations - hooks must be called unconditionally
  const listingHero = useExperimentVariation(AllowedExperimentKey.LISTING_HERO);
  const stickyHeaderTabs = useExperimentVariation(AllowedExperimentKey.STICKY_HEADER_TABS);
  const connectionRequestForm = useExperimentVariation(AllowedExperimentKey.CONNECTION_REQUEST_FORM);
  const zoopraisalMortgageCta = useExperimentVariation(AllowedExperimentKey.ZOOPRAISAL_MORTGAGE_CTA);

  // Track experiment views for analytics (once per experiment per page load)
  useExperimentTracking(AllowedExperimentKey.LISTING_HERO, listingHero.variationId, tenantId);
  useExperimentTracking(AllowedExperimentKey.STICKY_HEADER_TABS, stickyHeaderTabs.variationId, tenantId);
  useExperimentTracking(
    AllowedExperimentKey.CONNECTION_REQUEST_FORM,
    connectionRequestForm.variationId,
    tenantId
  );
  useExperimentTracking(AllowedExperimentKey.ZOOPRAISAL_MORTGAGE_CTA, zoopraisalMortgageCta.variationId, tenantId);

  // Build experiment state object with null safety
  const experimentState: ExperimentState = useMemo(
    () => ({
      [AllowedExperimentKey.LISTING_HERO]: listingHero?.showVariation ?? false,
      [AllowedExperimentKey.STICKY_HEADER_TABS]: stickyHeaderTabs?.showVariation ?? false,
      [AllowedExperimentKey.CONNECTION_REQUEST_FORM]:
        connectionRequestForm?.showVariation ?? false,
      [AllowedExperimentKey.ZOOPRAISAL_MORTGAGE_CTA]: zoopraisalMortgageCta?.showVariation ?? false,
    }),
    [
      listingHero?.showVariation,
      stickyHeaderTabs?.showVariation,
      connectionRequestForm?.showVariation,
      zoopraisalMortgageCta?.showVariation,
    ]
  );

  // Create visibility manager instance with viewport
  const visibility = useMemo(
    () => createExperimentElementVisibility(experimentState, viewport),
    [experimentState, viewport]
  );

  return visibility;
}
